import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

// All RCA/Query traffic goes through our backend. qtqms doesn't send
// CORS for cross-origin browsers, so every call — create, list, detail
// — has to be proxied server-to-server.
//
//   POST /queries/rca                                    — create (multipart)
//   GET  /queries/rca?clientId=X                         — list
//   GET  /queries/rca?clientId=X&visitor_id=Y            — detail (patient)
//   GET  /queries/rca?clientId=X&nc_id=Z                 — detail (general)

export const queriesApi = createApi({
  reducerPath: 'queriesApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  tagTypes: ['Rca', 'Concern'],
  endpoints: (builder) => ({
    // POST — multipart with `state` + `files[]`. FormData is passed
    // through untouched; RTK Query lets the browser set the boundary.
    createRcaInvestigation: builder.mutation({
      query: (formData) => ({
        url: '/queries/rca',
        method: 'POST',
        body: formData,
      }),
      // After creating a new query, invalidate the list so it refetches.
      invalidatesTags: ['Rca'],
    }),

    // GET list — one record per doctor's query.
    listRcaInvestigations: builder.query({
      query: ({ clientId }) =>
        `/queries/rca?clientId=${encodeURIComponent(clientId)}`,
      providesTags: ['Rca'],
    }),

    // GET detail — keyed by visitor_id (patient-specific) or nc_id
    // (general). Caller passes whichever is available.
    // Tagged 'Rca' so submitting a new query (which invalidates 'Rca')
    // also refreshes any open detail subscription — important for the
    // ReportDetailModal which polls this endpoint to decide between
    // "Raise Query" CTA and inline details panel.
    getRcaInvestigation: builder.query({
      query: ({ clientId, visitor_id, nc_id }) => {
        const qs = new URLSearchParams({ clientId });
        if (visitor_id) qs.set('visitor_id', visitor_id);
        if (nc_id) qs.set('nc_id', nc_id);
        return `/queries/rca?${qs.toString()}`;
      },
      providesTags: ['Rca'],
    }),

    // ── Report Queries, from Trust OPM ────────────────────────────────
    // Every portal query is a customer complaint in OPM. The list, the
    // detail with the thread and the reply all go to OPM through our
    // backend (portalComplaintsController). Tagged 'Rca' so a new query
    // (createRcaInvestigation) and a reply refresh the list and badges.
    //   GET  /queries/complaints?clientId=X
    //   GET  /queries/complaints/:id            (id = CP-2610-00012)
    //   POST /queries/complaints/:id/replies    { message }
    // Search, status filter and paging are done by OPM: q, status
    // (received | in_progress | resolved | closed), page, per_page. The
    // response carries `counts` per status for the tabs and the badge.
    listPortalComplaints: builder.query({
      query: ({ clientId, q = '', status = '', page = 1, per_page = 20 }) => {
        const qs = new URLSearchParams({ clientId });
        if (q) qs.set('q', q);
        if (status) qs.set('status', status);
        if (page && page !== 1) qs.set('page', String(page));
        if (per_page) qs.set('per_page', String(per_page));
        return `/queries/complaints?${qs.toString()}`;
      },
      providesTags: ['Rca'],
    }),
    getPortalComplaint: builder.query({
      query: ({ id }) => `/queries/complaints/${encodeURIComponent(id)}`,
      providesTags: ['Rca'],
    }),
    replyPortalComplaint: builder.mutation({
      query: ({ id, message }) => ({
        url: `/queries/complaints/${encodeURIComponent(id)}/replies`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: ['Rca'],
    }),

    // ── Sample Concerns (TL-CSD-SCR), from Trust OPM ──────────────────
    // TrustLab's accessioning logs a concern in OPM when a sample arrives
    // without a label or proper information; the centre answers here.
    //   GET  /sample-concerns?q=&status=&page=&per_page=
    //   GET  /sample-concerns/:no
    //   POST /sample-concerns/:no/replies   multipart: name, role, type, text, photos[]
    listSampleConcerns: builder.query({
      query: ({ q = '', status = '', page = 1, per_page = 20 } = {}) => {
        const qs = new URLSearchParams();
        if (q) qs.set('q', q);
        if (status) qs.set('status', status);
        qs.set('page', String(page));
        qs.set('per_page', String(per_page));
        return `/sample-concerns?${qs.toString()}`;
      },
      providesTags: ['Concern'],
    }),
    getSampleConcern: builder.query({
      query: ({ no }) => `/sample-concerns/${encodeURIComponent(no)}`,
      providesTags: ['Concern'],
    }),
    replySampleConcern: builder.mutation({
      query: ({ no, formData }) => ({
        url: `/sample-concerns/${encodeURIComponent(no)}/replies`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Concern'],
    }),
  }),
});

export const {
  useCreateRcaInvestigationMutation,
  useListRcaInvestigationsQuery,
  useGetRcaInvestigationQuery,
  useListPortalComplaintsQuery,
  useGetPortalComplaintQuery,
  useReplyPortalComplaintMutation,
  useListSampleConcernsQuery,
  useGetSampleConcernQuery,
  useReplySampleConcernMutation,
} = queriesApi;
